<template>
    <ConfirmationModal
        additional-class-name="ResendInviteModal"
        title="Resend Welcome Email"
        yes="Resend"
        no="Cancel"
        :progress="submitting"
        allow-close
        @input="onConfirm"
        @close="abort()"
    >
        You are about to resend the email to <strong>{{ user.email }}</strong>.
        <div class="ResendInviteModal__note">
            <FontAwesomeIcon :icon="faClock" />
            <span>
                Last email sent <strong>{{ lastEmailSent }}</strong>
            </span>
        </div>
    </ConfirmationModal>
</template>

<style lang="scss">
.ResendInviteModal {
    &__note {
        display: flex;
        align-items: center;
        margin-top: 1.5rem;
        color: #788ea1;

        svg {
            margin-right: 5px;
        }
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import moment from 'moment';

    import { faClock } from '@fortawesome/free-regular-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { User } from '@evidentid/tprm-portal-lib/models/user-management';
    import delay from '@evidentid/universal-framework/delay';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import { useUserManagementStore } from '@/modules/user-management/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: {
            ConfirmationModal,
            FontAwesomeIcon,
        },
        emits: [ 'abort', 'finish' ],
    })
    export default class ResendInviteProcedure extends Vue {
        private faClock = faClock;

        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: Object })
        private user!: User;
        // TODO[PRODUCT-24516]: use a proper fix instead of delay
        private delayLoading = false;

        private get status(): { status: OperationStatus, error: any } {
            return useUserManagementStore().resendInviteStatus[this.rpName]?.[this.user.id] || {
                status: OperationStatus.uninitialized,
                error: null,
            };
        }

        private get lastEmailSent(): string {
            return moment.utc(this.user.inviteEmailTimestamp).local().calendar();
        }

        private get submitting() {
            return this.status.status === OperationStatus.loading || this.delayLoading;
        }

        private success(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: true });
        }

        private error(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: false });
        }

        private abort(): void {
            this.$emit('abort');
        }

        private onConfirm(confirmed: boolean): void {
            if (confirmed) {
                this.resendInvite();
            } else {
                this.abort();
            }
        }

        private async resendInvite(): Promise<void> {
            if (this.status.status === OperationStatus.loading) {
                return;
            }

            await useUserManagementStore().resendInvite({
                rpName: this.rpName,
                id: this.user.id,
            });
            // TODO[PRODUCT-24516]: use a proper fix instead of delay
            this.delayLoading = true;
            await delay(1000);
            this.delayLoading = false;

            const { status } = this.status;
            if (status === OperationStatus.success) {
                this.success(`Welcome email was successfully resent to ${this.user.email}`);
                this.$emit('finish');
            } else {
                this.error('Oops, something went wrong. Please try again!');
            }
        }
    }
</script>
